import { cn } from '@/lib/utils';
import { Button } from '@/ui/shadcn/button';
import { TableHead } from '@/ui/shadcn/table';
import { Icon } from '../../icons/Icon';

/** The direction of the active column, in `aria-sort`'s own words. */
export type SortDirection = 'ascending' | 'descending';

export interface SortableHeaderCellProps {
    /** The column's header text, sentence case (design-system §9); also the button's accessible name. */
    readonly label: string;
    /** The active column's direction, or `null` when the list is sorted by another column. */
    readonly direction: SortDirection | null;
    /**
     * The user asked to sort by this column. The caller decides the new order — the active column
     * flips its direction, another column sorts ascending (F1-b WI-1.1 plan Decision 18) — and keeps
     * it in the URL, so a sorted view can be linked and survives a visit to a record (§6).
     */
    readonly onSort: () => void;
    /** The test hook, forwarded to the `<button>` inside the cell. */
    readonly 'data-testid'?: string;
    /**
     * The header cell's own classes — the table's header look, and where the table pins its header row
     * or first column, that pinning. The cell is the shadcn `TableHead`. `undefined` is allowed, because a
     * CSS module's class reads as `string | undefined`.
     */
    readonly className?: string | undefined;
    /** `end` for a figure column, so the label sits over right-aligned numbers (§9 numerics). */
    readonly align?: 'start' | 'end';
}

/**
 * Design-system §6's sortable header (*Data table (F32) → Sortable header*), on the shadcn Table parts
 * (AD #39): **the header cell's content is one ghost `Button`, a chevron shows the direction and only the
 * active column shows one, and `aria-sort` is on the `<th>`** — on the active one only, so a screen reader
 * announces one sorted column. The button takes the header's own type and colour from the cell, so a
 * sortable header looks as the table's other headers do. It has no underline on hover, since it is a control and
 * not a link: the chevron hint below is what marks a column as sortable.
 *
 * A column that is not the sorted one shows a faint up-and-down chevron (`sortable`) while the pointer is over it
 * or it has keyboard focus, so a reader can tell which columns sort before trying one; at rest only the sorted
 * column carries a chevron. The hint is placed beside the label out of the flow — after it, or before it in a
 * right-aligned figure column — so it takes no room: the label never moves and the header never wraps for it.
 * Like every glyph it is `aria-hidden`: the button's name is the label, and `aria-sort` says which column is
 * sorted.
 *
 * The table passes its header classes in `className`; the cell's padding stays the table's, and the button
 * fills the cell's content box, at least 24px tall (WCAG 2.2 SC 2.5.8). Equal negative block margins of half
 * the difference between that 24px and one line (`1lh`) put its label on the same line as a plain header's in
 * a table that aligns its headers to the top or bottom; the button keeps its 24px.
 *
 * It has no disabled or busy state: sorting a list that is loading only replaces the request in flight.
 */
export function SortableHeaderCell({
    label,
    direction,
    onSort,
    'data-testid': testId,
    className,
    align = 'start',
}: SortableHeaderCellProps) {
    return (
        <TableHead aria-sort={direction ?? undefined} className={className} scope="col">
            <Button
                className={cn(
                    'group/sort h-auto min-h-(--et-space-7) my-[calc((1lh-var(--et-space-7))/2)] w-full gap-(--et-space-2) rounded-sm p-0 font-[inherit] text-[length:inherit] leading-[inherit] font-semibold whitespace-normal text-foreground hover:bg-transparent hover:text-foreground has-[>svg]:px-0',
                    align === 'end' ? 'justify-end text-right' : 'justify-start text-left',
                )}
                data-testid={testId}
                onClick={onSort}
                variant="ghost"
            >
                {direction === null ? (
                    <span className="relative">
                        {label}
                        <span
                            className={cn(
                                'pointer-events-none absolute top-1/2 inline-flex -translate-y-1/2 text-muted-foreground opacity-0 transition-opacity group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100 motion-reduce:transition-none',
                                align === 'end'
                                    ? 'right-full mr-(--et-space-1)'
                                    : 'left-full ml-(--et-space-1)',
                            )}
                            data-sort-hint=""
                        >
                            <Icon name="sortable" size="sm" />
                        </span>
                    </span>
                ) : (
                    <>
                        {label}
                        <Icon
                            emphasis="bold"
                            name={direction === 'ascending' ? 'sortAscending' : 'sortDescending'}
                            size="sm"
                        />
                    </>
                )}
            </Button>
        </TableHead>
    );
}
